import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { CalendarIcon, RefreshCw } from 'lucide-react';
import { CTraderSignalFilters as FiltersType } from '@/services/ctrader-signal-service';

interface CTraderSignalFiltersProps {
  filters: FiltersType;
  onFiltersChange: (filters: FiltersType) => void;
  onRefresh: () => void;
  isLoading?: boolean;
}

export const CTraderSignalFilters = ({ 
  filters, 
  onFiltersChange, 
  onRefresh, 
  isLoading 
}: CTraderSignalFiltersProps) => {
  const handleFilterChange = (key: keyof FiltersType, value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      status: 'all'
    });
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="space-y-2">
            <Label htmlFor="status-filter">Status</Label>
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="OPEN">Completed</SelectItem>
                <SelectItem value="PROCESS">Processing</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="symbol-filter">Symbol</Label>
            <Input
              id="symbol-filter"
              placeholder="e.g., EURUSD"
              value={filters.symbol || ''}
              onChange={(e) => handleFilterChange('symbol', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-filter">Account ID</Label>
            <Input
              id="account-filter"
              placeholder="Account ID"
              value={filters.accountId || ''}
              onChange={(e) => handleFilterChange('accountId', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date-from">Date From</Label>
            <Input
              id="date-from"
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date-to">Date To</Label>
            <Input
              id="date-to"
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <Button
            variant="outline"
            onClick={clearFilters}
            className="text-sm"
          >
            Clear Filters
          </Button>
          
          <Button
            variant="outline"
            onClick={onRefresh}
            disabled={isLoading}
            className="text-sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};